import { useState, type MouseEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { Menu, X, Users, KeyRound, UserPen, LogOut } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { AvatarMenu } from './AvatarMenu';

import flagUSA from '../assets/wordpress-marketing/region-flags/usa.svg';
import flagCanada from '../assets/wordpress-marketing/region-flags/canada.svg';
import flagIndia from '../assets/wordpress-marketing/region-flags/india.svg';
import flagThailand from '../assets/wordpress-marketing/region-flags/thailand.svg';
import flagUN from '../assets/wordpress-marketing/region-flags/circle.svg';

interface RegionFlag {
  name: string;
  src: string;
  /** Optional override; falls back to "Available in <name>" — used for the UN globe
   *  which is a symbolic global indicator rather than an operating region. */
  title?: string;
  /** Bumps the rendered icon up to compensate for SVGs with internal whitespace
   *  (the UN globe has padding inside its viewBox). Layout box stays the same so
   *  flag-to-flag spacing isn't disturbed. */
  oversized?: boolean;
}

const REGION_FLAGS: readonly RegionFlag[] = [
  { name: 'USA', src: flagUSA },
  { name: 'Canada', src: flagCanada },
  { name: 'India', src: flagIndia },
  { name: 'Thailand', src: flagThailand },
  { name: 'United Nations', src: flagUN, title: 'United Nations', oversized: true },
];

/**
 * Phase 11 — region flags shown on every page so visitors immediately see the four
 * regions QuantumHire serves. Mobile renders them inside the hamburger drawer
 * so we don't crowd the compact navbar.
 */
const RegionFlags = ({ size = 'md' }: { size?: 'md' | 'lg' }) => {
  const cls = size === 'lg' ? 'w-8 h-8' : 'w-6 h-6';
  return (
    <div className="flex items-center gap-2.5">
      {REGION_FLAGS.map((flag) => (
        <img
          key={flag.name}
          src={flag.src}
          alt={flag.name}
          title={flag.title ?? `Available in ${flag.name}`}
          className={`${cls} rounded-full object-cover shadow-sm ring-1 ring-white/20${flag.oversized ? ' scale-[1.15]' : ''}`}
        />
      ))}
    </div>
  );
};

const HomeBrand = () => (
  <Link to="/" className="flex items-center" aria-label="QuantumHire — home">
    <img
      src="/logos/quantumhire-logo-poppins.png"
      alt="QuantumHire"
      className="block h-auto w-[150px] origin-left scale-[1.125]"
    />
  </Link>
);

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const pathname = location.pathname;
  const { candidate, isLoading, signOut } = useAuth();
  const isHome = pathname === '/';
  const isEmployer = pathname.startsWith('/employers');
  const isResources = pathname === '/resources' || pathname.startsWith('/resources/');
  const isProduct = pathname === '/product';
  const normalizedPath = pathname.replace(/\/+$/, '') || '/';
  const isJobSeekersNavActive =
    normalizedPath === '/job-seekers' || normalizedPath.startsWith('/job-seekers/');
  const usesLegacyNavbar =
    normalizedPath === '/' ||
    normalizedPath === '/legacy-home' ||
    normalizedPath === '/pricing' ||
    normalizedPath === '/about' ||
    normalizedPath === '/careers' ||
    normalizedPath === '/profile' ||
    normalizedPath === '/sentry-test' ||
    normalizedPath === '/jobs' ||
    normalizedPath.startsWith('/jobs/');

  /**
   * Mobile equivalent of the avatar dropdown. The hamburger menu owns its own scroll, so we
   * inline the same items here rather than nesting the dropdown component (which would float
   * over the menu and feel awkward on small viewports).
   */
  const renderSignedInMobile = () => {
    if (!candidate) return null;
    return (
      <div className="flex flex-col gap-1 pt-4 border-t border-gray-100">
        <div className="px-2 pb-3">
          <p className="text-sm font-bold text-brand-primary truncate">
            {candidate.full_name || 'Candidate'}
          </p>
          <p className="text-xs text-brand-muted truncate">{candidate.email}</p>
        </div>
        <Link
          to="/profile"
          onClick={() => setIsOpen(false)}
          className="flex items-center gap-3 px-2 py-3 text-sm font-medium text-brand-primary"
        >
          <UserPen className="w-4 h-4" />
          Update Profile
        </Link>
        <Link
          to="/change-password"
          onClick={() => setIsOpen(false)}
          className="flex items-center gap-3 px-2 py-3 text-sm font-medium text-brand-primary"
        >
          <KeyRound className="w-4 h-4" />
          Change Password
        </Link>
        <button
          type="button"
          onClick={() => {
            void signOut();
            setIsOpen(false);
          }}
          className="flex items-center gap-3 px-2 py-3 text-sm font-medium text-brand-primary text-left"
        >
          <LogOut className="w-4 h-4" />
          Sign Out
        </button>
      </div>
    );
  };

  const renderSignedOutCtas = (mobile: boolean) => (
    <div
      className={
        mobile ? 'flex flex-col gap-3 pt-4 border-t border-gray-100' : 'flex items-center gap-4'
      }
    >
      <Link
        to="/signin"
        onClick={() => setIsOpen(false)}
        className={
          mobile
            ? 'w-full py-3 text-center text-sm font-bold text-brand-primary border border-gray-200 rounded-2xl'
            : 'text-sm font-bold text-gray-300 hover:text-white transition-colors'
        }
      >
        Sign in
      </Link>
      <Link
        to="/signup"
        onClick={() => setIsOpen(false)}
        className={
          mobile
            ? 'w-full py-3 text-center text-sm font-bold text-white bg-brand-primary rounded-2xl flex items-center justify-center gap-2'
            : 'px-5 py-2.5 bg-brand-accent text-brand-primary rounded-full text-sm font-bold hover:bg-brand-accent/90 transition-all shadow-lg flex items-center gap-2'
        }
      >
        <Users className="w-4 h-4" />
        Create account
      </Link>
    </div>
  );

  const scrollToHiringForm = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setIsOpen(false);
    document.getElementById('start-hiring')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  if (!usesLegacyNavbar) {
    return (
      <nav className="sticky inset-x-0 top-0 z-50 w-full border-b border-black/10 bg-[#f5f6f2]/90 backdrop-blur-[10px]">
        <div className="relative mx-auto flex max-w-[1180px] items-center justify-between px-8 py-[18px]">
          {/* Logo */}
          <HomeBrand />

          {/* Desktop navigation */}
          <div className="absolute left-1/2 hidden -translate-x-1/2 min-[861px]:flex items-center gap-9 text-sm text-[#3a3c35]">
            <Link
              to="/employers"
              className={`relative py-2 transition-colors hover:text-[#14150f] ${isEmployer ? 'text-[#14150f]' : ''}`}
            >
              For employers
              {isEmployer && (
                <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#0b5c22]" />
              )}
            </Link>

            <Link
              to="/job-seekers"
              className={`relative py-2 transition-colors hover:text-[#14150f] ${isJobSeekersNavActive ? 'text-[#14150f]' : ''}`}
            >
              For jobseekers
              {isJobSeekersNavActive && (
                <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#0b5c22]" />
              )}
            </Link>

            <Link
              to="/product"
              className={`relative py-2 transition-colors hover:text-[#14150f] ${isProduct ? 'text-[#14150f]' : ''}`}
            >
              Product
              {isProduct && (
                <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#0b5c22]" />
              )}
            </Link>

            <Link
              to="/employer-branding"
              className={`relative py-2 transition-colors hover:text-[#14150f] ${
                pathname === '/employer-branding' ? 'text-[#14150f]' : ''
              }`}
            >
              Employer Branding
              {pathname === '/employer-branding' && (
                <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#0b5c22]" />
              )}
            </Link>

            <Link
              to="/resources"
              className={`relative py-2 transition-colors hover:text-[#14150f] ${isResources ? 'text-[#14150f]' : ''}`}
            >
              Resources
              {isResources && (
                <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#0b5c22]" />
              )}
            </Link>
          </div>

          {/* CTA */}
          <div className="hidden min-[861px]:flex items-center gap-4">
            {isEmployer ? (
              <Link
                to="/employers#start-hiring"
                onClick={isEmployer ? scrollToHiringForm : undefined}
                className="flex items-center gap-2 rounded-[2px] bg-[#14150f] px-[18px] py-[10px] font-['JetBrains_Mono',monospace] text-[12.5px] text-white transition-colors hover:bg-[#0b5c22]"
              >
                <span className="h-[7px] w-[7px] rounded-full bg-[#1caa3c]" />
                <span>Send us a req</span>
              </Link>
            ) : isLoading ? (
              <span className="h-9 w-9 animate-pulse rounded-full bg-black/10" aria-hidden="true" />
            ) : candidate ? (
              <AvatarMenu candidate={candidate} onSignOut={() => void signOut()} />
            ) : isJobSeekersNavActive ? (
              <>
                <Link
                  to="/signin"
                  className="text-sm font-medium text-[#3a3c35] hover:text-[#14150f]"
                >
                  Sign in
                </Link>
                <Link
                  to="/signup"
                  className="rounded-[2px] bg-[#14150f] px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-[#176d2e]"
                >
                  Create account
                </Link>
              </>
            ) : null}
          </div>

          {/* Mobile */}
          <div className="flex items-center gap-3 min-[861px]:hidden">
            {isEmployer ? (
              <Link
                to="/employers#start-hiring"
                onClick={isEmployer ? scrollToHiringForm : undefined}
                className="hidden rounded-[2px] bg-[#14150f] px-[18px] py-[10px] font-['JetBrains_Mono',monospace] text-[12.5px] text-white"
              >
                Send us a req
              </Link>
            ) : !isLoading && candidate ? (
              <AvatarMenu candidate={candidate} onSignOut={() => void signOut()} />
            ) : null}

            <button
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              className="flex h-9 w-9 shrink-0 flex-col justify-center gap-[5px] border-0 bg-transparent p-0"
              aria-label={isOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isOpen}
            >
              <span
                className={`h-0.5 w-[22px] rounded-sm bg-[#14150f] transition-transform duration-200 ${
                  isOpen ? 'translate-y-[7px] rotate-45' : ''
                }`}
              />

              <span
                className={`h-0.5 w-[22px] rounded-sm bg-[#14150f] transition-opacity duration-200 ${
                  isOpen ? 'opacity-0' : ''
                }`}
              />

              <span
                className={`h-0.5 w-[22px] rounded-sm bg-[#14150f] transition-transform duration-200 ${
                  isOpen ? '-translate-y-[7px] -rotate-45' : ''
                }`}
              />
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden border-t border-black/10 bg-[#f5f6f2] min-[861px]:hidden"
            >
              <div className="flex flex-col">
                <Link
                  to="/employers"
                  onClick={() => setIsOpen(false)}
                  className="border-b border-black/10 px-8 py-4 text-[15px] text-[#14150f]"
                >
                  For employers
                </Link>

                <Link
                  to="/job-seekers"
                  onClick={() => setIsOpen(false)}
                  className="border-b border-black/10 px-8 py-4 text-[15px] text-[#3a3c35]"
                >
                  For jobseekers
                </Link>
                <Link
                  to="/product"
                  onClick={() => setIsOpen(false)}
                  className={`border-b border-black/10 px-8 py-4 text-[15px] ${isProduct ? 'bg-[#e5f5e8] font-semibold text-[#0b5c22]' : 'text-[#3a3c35]'}`}
                >
                  Product
                </Link>

                <Link
                  to="/employer-branding"
                  onClick={() => setIsOpen(false)}
                  className={`border-b border-black/10 px-8 py-4 text-[15px] ${
                    pathname === '/employer-branding'
                      ? 'bg-[#e5f5e8] font-semibold text-[#0b5c22]'
                      : 'text-[#3a3c35]'
                  }`}
                >
                  Employer Branding
                </Link>

                <Link
                  to="/resources"
                  onClick={() => setIsOpen(false)}
                  className={`border-b border-black/10 px-8 py-4 text-[15px] ${isResources ? 'bg-[#e5f5e8] font-semibold text-[#0b5c22]' : 'text-[#3a3c35]'}`}
                >
                  Resources
                </Link>

                {isEmployer ? (
                  <Link
                    to="/employers#start-hiring"
                    onClick={isEmployer ? scrollToHiringForm : undefined}
                    className="mx-8 mb-5 mt-4 flex items-center justify-center gap-2 rounded-[2px] bg-[#14150f] px-[18px] py-[13px] font-['JetBrains_Mono',monospace] text-[13px] font-semibold text-white"
                  >
                    <span className="h-[7px] w-[7px] rounded-full bg-[#1caa3c]" />
                    <span className="font-['JetBrains_Mono',monospace]">Send us a req</span>
                  </Link>
                ) : !isLoading && candidate ? (
                  <div className="px-6 pb-5">{renderSignedInMobile()}</div>
                ) : !isLoading && isJobSeekersNavActive ? (
                  <div className="px-6 pb-5">{renderSignedOutCtas(true)}</div>
                ) : null}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    );
  }

  if (isHome) {
    return (
      <nav className="sticky inset-x-0 top-0 z-50 w-full rounded-none border-b border-black/10 bg-[#f5f6f2]/90 leading-[normal] backdrop-blur-[10px]">
        <div className="relative mx-auto flex max-w-[1180px] items-center justify-between min-[1180px]:gap-6 px-8 py-[18px]">
          <HomeBrand />

          <div className="hidden shrink-0 min-[1180px]:flex items-center gap-9 whitespace-nowrap text-sm text-[#3a3c35]">
            <Link to="/employers" className="transition-colors hover:text-[#14150f]">
              For employers
            </Link>
            <Link to="/job-seekers" className="transition-colors hover:text-[#14150f]">
              For jobseekers
            </Link>
            <Link to="/product" className="transition-colors hover:text-[#14150f]">
              Product
            </Link>
            <Link to="/employer-branding" className="transition-colors hover:text-[#14150f]">
              Employer Branding
            </Link>
            <Link to="/resources" className="transition-colors hover:text-[#14150f]">
              Resources
            </Link>
          </div>

          <div className="hidden shrink-0 min-[1180px]:flex items-center gap-5 whitespace-nowrap">
            <span className="font-['JetBrains_Mono',monospace] text-xs text-[#6b6f68]">
              US · Canada · India · Thailand
            </span>
            {isLoading ? (
              <span className="h-9 w-9 animate-pulse rounded-full bg-black/10" aria-hidden="true" />
            ) : candidate ? (
              <AvatarMenu candidate={candidate} onSignOut={() => void signOut()} />
            ) : null}
          </div>

          <div className="flex shrink-0 items-center gap-3 min-[1180px]:hidden">
            {!isLoading && candidate && (
              <AvatarMenu candidate={candidate} onSignOut={() => void signOut()} />
            )}
            <button
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              className="flex h-9 w-9 shrink-0 flex-col justify-center gap-[5px] border-0 bg-transparent p-0"
              aria-label={isOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isOpen}
            >
              <span
                className={`h-0.5 w-[22px] rounded-sm bg-[#14150f] transition-transform duration-200 ${isOpen ? 'translate-y-[7px] rotate-45' : ''}`}
              />
              <span
                className={`h-0.5 w-[22px] rounded-sm bg-[#14150f] transition-opacity duration-200 ${isOpen ? 'opacity-0' : ''}`}
              />
              <span
                className={`h-0.5 w-[22px] rounded-sm bg-[#14150f] transition-transform duration-200 ${isOpen ? '-translate-y-[7px] -rotate-45' : ''}`}
              />
            </button>
          </div>
        </div>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden border-t border-black/10 bg-[#f5f6f2] min-[1180px]:hidden"
            >
              <div className="flex flex-col">
                <Link
                  to="/employers"
                  onClick={() => setIsOpen(false)}
                  className="border-b border-black/10 px-8 py-4 text-[15px] text-[#3a3c35]"
                >
                  For employers
                </Link>
                <Link
                  to="/job-seekers"
                  onClick={() => setIsOpen(false)}
                  className="border-b border-black/10 px-8 py-4 text-[15px] text-[#3a3c35]"
                >
                  For jobseekers
                </Link>
                <Link
                  to="/product"
                  onClick={() => setIsOpen(false)}
                  className="border-b border-black/10 px-8 py-4 text-[15px] text-[#3a3c35]"
                >
                  Product
                </Link>
                <Link
                  to="/employer-branding"
                  onClick={() => setIsOpen(false)}
                  className="border-b border-black/10 px-8 py-4 text-[15px] text-[#3a3c35]"
                >
                  Employer Branding
                </Link>
                <Link
                  to="/resources"
                  onClick={() => setIsOpen(false)}
                  className="border-b border-black/10 px-8 py-4 text-[15px] text-[#3a3c35]"
                >
                  Resources
                </Link>
                <div className="px-8 pb-[22px] pt-1 font-['JetBrains_Mono',monospace] text-xs text-[#6b6f68]">
                  US · Canada · India · Thailand
                </div>
                {!isLoading && candidate && (
                  <div className="px-6 pb-5">{renderSignedInMobile()}</div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    );
  }

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-black border-b border-black/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <div className="flex items-center gap-12">
            <Link to="/" className="flex items-center" aria-label="QuantumHire — home">
              <img
                src="/logos/quantumhire-logo-rectangle-dark.png"
                alt="QuantumHire"
                className="block h-10 w-auto md:h-12"
              />
            </Link>

            <div className="hidden md:flex items-center gap-10 font-medium text-sm">
              <Link
                to="/employers"
                className={`hover:text-brand-accent transition-colors ${pathname.startsWith('/employers') ? 'text-brand-accent font-bold' : 'text-gray-300'}`}
              >
                Companies
              </Link>
              <Link
                to="/job-seekers"
                className={`hover:text-brand-accent transition-colors ${pathname.startsWith('/job-seekers') ? 'text-brand-accent font-bold' : 'text-gray-300'}`}
              >
                Jobs
              </Link>
              <Link
                to="/product"
                className={`hover:text-brand-accent transition-colors ${isProduct ? 'text-brand-accent font-bold' : 'text-gray-300'}`}
              >
                Product
              </Link>
              {/* Pricing menu hidden 2026-05-04 — route still exists at /pricing. */}
              {/*
              <Link
                to="/pricing"
                className={`hover:text-brand-accent transition-colors ${pathname === '/pricing' ? 'text-brand-accent font-bold' : 'text-gray-300'}`}
              >
                Pricing
              </Link>
              */}
              <Link
                to="/job-seekers"
                className={`hover:text-brand-accent transition-colors ${pathname === '/job-seekers' ? 'text-brand-accent font-bold' : 'text-gray-300'}`}
              >
                For Job Seekers
              </Link>
            </div>
          </div>

          <div className="hidden md:flex items-center gap-5">
            <RegionFlags />
            {isLoading ? (
              <div className="w-9 h-9 rounded-full bg-white/10 animate-pulse" aria-hidden />
            ) : candidate ? (
              <AvatarMenu candidate={candidate} onSignOut={() => void signOut()} />
            ) : !isJobSeekersNavActive ? null : (
              renderSignedOutCtas(false)
            )}
          </div>

          <div className="md:hidden flex items-center gap-3">
            {!isLoading && candidate && (
              <AvatarMenu candidate={candidate} onSignOut={() => void signOut()} />
            )}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 text-white"
              aria-label={isOpen ? 'Close menu' : 'Open menu'}
            >
              {isOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-b border-gray-100 px-4 py-8 flex flex-col gap-6 overflow-hidden"
          >
            <Link to="/employers" onClick={() => setIsOpen(false)} className="text-lg font-medium">
              Companies
            </Link>
            <Link to="/job-seekers" onClick={() => setIsOpen(false)} className="text-lg font-medium">
              Jobs
            </Link>
            <Link to="/product" onClick={() => setIsOpen(false)} className="text-lg font-medium">
              Product
            </Link>
            {/* Pricing menu hidden 2026-05-04 — route still exists at /pricing. */}
            {/*
            <Link to="/pricing" onClick={() => setIsOpen(false)} className="text-lg font-medium">
              Pricing
            </Link>
            */}
            <Link
              to="/job-seekers"
              onClick={() => setIsOpen(false)}
              className="text-lg font-medium"
            >
              For Job Seekers
            </Link>

            <div className="pt-4 border-t border-gray-100">
              <p className="text-xs font-bold uppercase tracking-wider text-brand-muted mb-3">
                Available regions
              </p>
              <RegionFlags size="lg" />
            </div>

            {isLoading
              ? null
              : candidate
                ? renderSignedInMobile()
                : !isJobSeekersNavActive
                  ? null
                  : renderSignedOutCtas(true)}
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};
